import React from 'react';
import { AccordionSection } from './components/AccordionSection';

const StatisticalMethodsExplanation: React.FC = () => {
  return (
    <div className="mt-10">
      <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Understanding The Statistical Methods</h3>
      <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
        Click on any section below to learn more about the statistical methods used in our calculator
      </p>
      
      <div className="overflow-hidden">
        <AccordionSection
          title="Conversion Rate Tests (Two-Proportion Z-Test)"
          subtitle="For testing binary outcomes like clicks or purchases"
        >
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold">What this calculates:</h4>
              <p>The number of samples needed to detect changes in conversion rates</p>
            </div>
            
            <div>
              <h4 className="font-semibold">When to use this:</h4>
              <p>Select this when testing binary outcomes (e.g., clicked/didn&apos;t click, purchased/didn&apos;t purchase)</p>
            </div>
            
            <div>
              <h4 className="font-semibold">How it works:</h4>
              <p>Compares proportions between your control and variant groups, detecting effects in both directions</p>
            </div>
            
            <div>
              <h4 className="font-semibold">Formula used:</h4>
              <pre className="bg-gray-100 dark:bg-gray-700/70 p-3 rounded overflow-x-auto text-sm">
                Samples per variant = ((zα/2 + zβ)² × p̄ × (1-p̄) × 2) / (p₂-p₁)²
              </pre>
            </div>
            
            <div>
              <h4 className="font-semibold">Where:</h4>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>p₁ = your baseline conversion rate (entered as a percentage, used as a decimal in calculations)</li>
                <li>p₂ = expected conversion rate after applying your MDE</li>
                <li>p̄ = average of p₁ and p₂</li>
                <li>zα/2 = z-score for your selected confidence level</li>
                <li>zβ = z-score for your selected statistical power</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold">Assumptions:</h4>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>Your outcomes are binary (e.g., converted/didn&apos;t convert)</li>
                <li>Samples are independent (one user&apos;s behavior doesn&apos;t influence another&apos;s)</li>
                <li>Sample size is large enough for normal approximation (where np {'>'}  5 and n(1-p) {'>'}  5, meaning you need at least 5 expected conversions and 5 expected non-conversions in each group)</li>
                <li>Random assignment of users to variants</li>
              </ul>
            </div>
          </div>
        </AccordionSection>
        
        <AccordionSection
          title="Metric Tests (Two-Sample T-Test)"
          subtitle="For testing numerical outcomes like revenue or time spent"
        >
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold">What this calculates:</h4>
              <p>The number of samples needed to detect changes in continuous metrics</p>
            </div>
            
            <div>
              <h4 className="font-semibold">When to use this:</h4>
              <p>Select this when testing numerical outcomes (e.g., revenue, time spent, number of actions)</p>
            </div>
            
            <div>
              <h4 className="font-semibold">How it works:</h4>
              <p>Compares means between your control and variant groups, detecting effects in both directions</p>
            </div>
            
            <div>
              <h4 className="font-semibold">Formula used:</h4>
              <pre className="bg-gray-100 dark:bg-gray-700/70 p-3 rounded overflow-x-auto text-sm">
                Samples per variant = 2 × ((zα/2 + zβ)² × σ²) / δ²
              </pre>
            </div>
            
            <div>
              <h4 className="font-semibold">Where:</h4>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>σ² = variance (your entered standard deviation squared)</li>
                <li>δ = absolute difference to detect (your baseline × your MDE%)</li>
                <li>zα/2 = z-score for your selected confidence level</li>
                <li>zβ = z-score for your selected statistical power</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold">Assumptions:</h4>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>Your metric is approximately normally distributed</li>
                <li>Variances are equal or similar between control and variant groups</li>
                <li>Samples are independent</li>
                <li>Random assignment of users to variants</li>
                <li>Sample size is sufficiently large (typically n {'>'}  30 per group)</li>
              </ul>
            </div>
          </div>
        </AccordionSection>
        
        <AccordionSection
          title="Log Transformation (Log-Normal Distribution Adjustment)"
          subtitle="For right-skewed metrics like revenue or session duration"
        >
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold">What this calculates:</h4>
              <p>Adjusted sample size for right-skewed metrics</p>
            </div>
            
            <div>
              <h4 className="font-semibold">When to use this:</h4>
              <p>Enable this option when your metric has a long right tail (e.g., revenue, session duration)</p>
            </div>
            
            <div>
              <h4 className="font-semibold">How it works:</h4>
              <p>Applies a logarithmic transformation to better handle skewed distributions by approximating log-transformed parameters from your raw statistics</p>
            </div>
            
            <div>
              <h4 className="font-semibold">Formula used:</h4>
              <pre className="bg-gray-100 dark:bg-gray-700/70 p-3 rounded overflow-x-auto text-sm">
                Samples per variant = 2 × ((zα/2 + zβ)² × log(1 + CV²)) / (log(1 + MDE/100))²
              </pre>
            </div>
            
            <div>
              <h4 className="font-semibold">Where:</h4>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>CV = coefficient of variation (your standard deviation ÷ your baseline value)</li>
                <li>zα/2 = z-score for your selected confidence level</li>
                <li>zβ = z-score for your selected statistical power</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold">Important Accuracy Considerations:</h4>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>This method approximates log-transformed parameters from raw statistics</li>
                <li>For metrics with low to moderate skew (CV below 0.7), approximation error is typically rather small</li>
                <li>For highly skewed metrics or those with many outliers, error can be much higher leading to wrong sample size estimates</li>
                <li>Alternative approach: Log-transform your raw data first, calculate mean and standard deviation of the transformed values, then use those values in the form above with the log-normal toggle disabled</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold">Assumptions:</h4>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>Your metric follows a log-normal distribution</li>
                <li>Data is right-skewed (has a longer tail to the right)</li>
                <li>Values are strictly positive</li>
                <li>The coefficient of variation is stable across variants</li>
              </ul>
            </div>
          </div>
        </AccordionSection>
        
        <AccordionSection
          title="Multiple Variants Adjustment (Bonferroni Correction)"
          subtitle="For tests with more than two variants (A/B/n testing)"
        >
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold">What this calculates:</h4>
              <p>Increased sample size requirements when testing multiple variants</p>
            </div>
            
            <div>
              <h4 className="font-semibold">When to use this:</h4>
              <p>This is automatically applied when you enter more than 2 variants</p>
            </div>
            
            <div>
              <h4 className="font-semibold">How it works:</h4>
              <p>Applies the Bonferroni correction to maintain statistical validity</p>
            </div>
            
            <div>
              <h4 className="font-semibold">Formula used:</h4>
              <pre className="bg-gray-100 dark:bg-gray-700/70 p-3 rounded overflow-x-auto text-sm">
                adjusted_α = α / (variants - 1)
              </pre>
            </div>
            
            <div>
              <h4 className="font-semibold">Where:</h4>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>α = significance level (100% - your confidence level)</li>
                <li>variants = number of test variations you entered (including control)</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold">Assumptions:</h4>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>All comparisons are equally important</li>
                <li>Each variant is compared only to the control (not to other variants)</li>
                <li>You want to control the family-wise error rate</li>
                <li>Independent tests between variants</li>
                <li>Note: Bonferroni correction is conservative and may increase the chance of missing real effects</li>
              </ul>
            </div>
          </div>
        </AccordionSection>
        
        <AccordionSection
          title="Test Duration Calculation (Traffic-Based Estimation)"
          subtitle="For estimating how long your test needs to run"
        >
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold">What this calculates:</h4>
              <p>How long your test needs to run based on your traffic</p>
            </div>
            
            <div>
              <h4 className="font-semibold">When to use this:</h4>
              <p>This is automatically calculated based on your inputs</p>
            </div>
            
            <div>
              <h4 className="font-semibold">How it works:</h4>
              <p>Divides your total required sample by your daily traffic</p>
            </div>
            
            <div>
              <h4 className="font-semibold">Formula used:</h4>
              <pre className="bg-gray-100 dark:bg-gray-700/70 p-3 rounded overflow-x-auto text-sm">
                Total sample size = Samples per variant × Number of variants
                Days to run = ⌈Total sample size ÷ Daily samples⌉
              </pre>
            </div>
            
            <div>
              <h4 className="font-semibold">Where:</h4>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>⌈x⌉ represents rounding up to the nearest whole day</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold">Assumptions:</h4>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>Traffic volume is consistent across the test period</li>
                <li>Users are randomly assigned to variants</li>
                <li>No significant seasonality effects during the test period</li>
                <li>The daily sample count represents unique users (or independent observations)</li>
                <li>Test runs continuously without interruption</li>
              </ul>
            </div>
          </div>
        </AccordionSection>
      </div>
    </div>
  );
};

export default StatisticalMethodsExplanation;

